// TODO: This is a temporary component to test the Stripe payment method modal. Remove before we go live.
import React, { useEffect, useState } from 'react';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { PaymentMethod, loadStripe } from '@stripe/stripe-js';

import { Button, Layout, Text } from 'ds';
import StripePaymentMethodModal from 'ds/StripePaymentMethodModal';
import { selectUser } from 'store/User/selectors';
import { useAppSelector } from 'store/hooks';

import { createSetupIntent, deletePaymentMethod, listPaymentMethods, setDefaultPaymentMethod } from './requests';

const stripePromise = loadStripe(
  process.env.REACT_APP_ENV === 'production' ? 'pk_live_FIjwb0XoD7GN4J2GCukoKk2u' : 'pk_test_IKX09H9MA4Vpme8wKsNUqrjQ'
);

const PaymentMethods: React.FC = () => {
  const user = useAppSelector(selectUser);
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>();
  const [modalIsVisible, setModalIsVisible] = useState(false);
  const [modalOrdinal, setModalOrdinal] = useState(0);

  useEffect(() => {
    listPaymentMethods().then(({ data }) => setPaymentMethods(data));
  }, []);

  if (!user || !paymentMethods) return null;

  const handleSubmit = async () => {
    // event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const bankAccountElement = elements.getElement(PaymentElement);
      if (!bankAccountElement) {
        setError('Bank account element not found');
        return;
      }

      const { error: setupError } = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: window.location.href,
          payment_method_data: {
            billing_details: {
              name: user.customer?.name || '',
              email: user.customer?.email || ''
            },
            allow_redisplay: 'always'
          }
        },
        redirect: 'if_required'
      });

      if (setupError) {
        setError(setupError.message || 'An error occurred');
        return;
      }

      await setDefaultPaymentMethod();

      listPaymentMethods().then(({ data }) => setPaymentMethods(data));
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const closeModal = () => {
    setModalIsVisible(false);
    setModalOrdinal(modalOrdinal + 1);
  };

  const onUpdate = () => {
    listPaymentMethods().then(({ data }) => setPaymentMethods(data));
  };

  return (
    <Layout padding={24} direction="column">
      <StripePaymentMethodModal
        key={modalOrdinal}
        isVisible={modalIsVisible}
        onClose={closeModal}
        onUpdate={onUpdate}
      />
      <Button size="md" type="primary" text="Launch modal" onClick={() => setModalIsVisible(true)} />
      {paymentMethods.length > 0 && (
        <Layout marginBottom={24}>
          <Text size="body-md">Your Payment Method</Text>
          <Layout direction="column">
            {paymentMethods.map(paymentMethod => (
              <Layout direction="row" gap={8} justify="space-between" align="center" key={paymentMethod.id}>
                <Layout direction="row" gap={8}>
                  <Text>Bank Account ending in {paymentMethod.us_bank_account?.last4}</Text>
                  <Text color="gray-500">•••• {paymentMethod.us_bank_account?.last4}</Text>
                </Layout>
                <Button
                  size="sm"
                  type="secondary"
                  text="Delete"
                  onClick={() => {
                    if (window.confirm('Are you sure you want to delete this payment method?')) {
                      deletePaymentMethod(paymentMethod.id)
                        .then(({ data }) => {
                          setPaymentMethods(data);
                        })
                        .catch(err => {
                          console.error('Error deleting payment method:', err);
                          alert('Failed to delete payment method. Please try again.');
                        });
                    }
                  }}
                />
              </Layout>
            ))}
          </Layout>
        </Layout>
      )}
      {paymentMethods.length === 0 && (
        <>
          <Text size="body-md">Add Payment Method</Text>

          <Layout maxWidth={480}>
            <form onSubmit={handleSubmit}>
              <Text size="body-sm">Please enter your US bank account information below:</Text>

              <Layout borderRadius={4} padding={16} marginBottom={24}>
                <PaymentElement
                  options={{
                    business: {
                      name: 'Codi'
                    },
                    fields: {
                      billingDetails: {
                        name: 'never',
                        email: 'never'
                      }
                    },
                    paymentMethodOrder: ['us_bank_account']
                  }}
                />
              </Layout>

              {error && (
                <Layout padding={16} marginBottom={16} borderRadius={4}>
                  <Text color="red-500">{error}</Text>
                </Layout>
              )}

              <Button
                size="md"
                type="primary"
                text="Add Bank Account"
                disabled={!stripe || loading}
                showSpinner={loading}
                onClick={handleSubmit}
              />
            </form>
          </Layout>
        </>
      )}
    </Layout>
  );
};

const PaymentMethodsWrapper: React.FC = () => {
  const [clientSecret, setClientSecret] = useState<string>();

  useEffect(() => {
    createSetupIntent().then(({ data }) => setClientSecret(data.clientSecret));
  }, []);

  if (!clientSecret) {
    return null; // or loading spinner
  }

  return (
    <Elements
      stripe={stripePromise}
      options={{
        clientSecret,
        appearance: {
          theme: 'stripe'
        }
      }}
    >
      <PaymentMethods />
    </Elements>
  );
};

export default PaymentMethodsWrapper;
